/** @format */
import { css } from 'styled-components';

import { largeTablet, smallMobile } from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

import { IMAGE_ASPECT_RATIO, StyledModelOverviewProps } from './index';
import { getModelOverviewGridTemplateColumns } from './style-helpers';

/**
 * Base styles for the model overview component
 *
 * @param {StyledModelOverviewProps} props The model overview properties object
 * @param {boolean} props.hasImages If the component has images to display
 * */
const baseStyles = ({
    hasSpecs,
    hasImages,
    isLoadingImages,
}: StyledModelOverviewProps) => {
    const invertedAspectRatio = (1 / IMAGE_ASPECT_RATIO) * 1;

    /* eslint-disable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
    return css`
        margin-bottom: 3rem; // Half button height
        --carousel__button-padding: var(--layout-spacing-24);

        .model-overview-wrapper {
            display: grid;
            align-items: center;
            gap: var(--layout-spacing-48);
            padding-bottom: var(--layout-spacing-24);
            grid-template-columns: ${getModelOverviewGridTemplateColumns({
                hasImages,
                isLoadingImages,
                isSmallTablet: false,
            })};
        }

        .model-overview-left {
            display: flex;
            flex-direction: column;
            min-width: 0;
            max-width: 63rem;
            margin: auto;
        }

        .model-overview-title {
            margin-bottom: var(--layout-spacing-12);
            font-weight: 300;
        }

        .model-overview-carousel {
            ${
                isLoadingImages &&
                css`
                    aspect-ratio: ${invertedAspectRatio};
                `
            }
        }

        .model-overview-skeleton {
            height: 100%;
        }

        .model-overview-specs {
            width: 100%;
            padding-top: 2.5rem;

            .skeleton-wrapper-desc {
                min-width: 10rem;
            }

            .skeleton-wrapper-term {
                min-width: 7rem;
            }
        }

        .model-overview-specs-title {
            text-align: center;
            margin-bottom: 3.6rem;
        }

        .model-overview-specs-dls {
            display: flex;
            margin: 0 auto 7rem;
            max-width: 103rem;
            justify-content: center;
        }

        .model-overview-specs-dl {
            margin: 0 var(--layout-spacing-24);
            max-width: 47rem;
            flex-grow: 1;
            flex-basis: 0;

            @media (-ms-high-contrast: none) {
                width: calc(50% - var(--layout-spacing-48));
                flex-basis: auto;
            }
        }

        .model-overview-specs-line {
            border-bottom: 1px solid var(--colors-border-tertiary);
        }

        .model-overview-key-features {
            font-weight: 600;
        }

        .model-overview-other-stock {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin: var(--layout-spacing-48) 0 0;

            strong {
                display: block;
                margin-bottom: var(--layout-spacing-4);
                ${theme.typography.body_normal_highlight}
            }
        }

        .model-overview-other-stock-product-list {
            margin: 0 calc(var(--layout-spacing-48) * -1);

            ul {
                padding: var(--layout-spacing-24) 0 var(--layout-spacing-48);

                li {
                    padding: 0 var(--layout-spacing-8);

                    &:last-of-type {
                        padding-right: 0;
                    }

                    &:first-of-type {
                        padding-left: 0;
                    }
                }
            }

            .scrollable-carousel-inner {
                padding: 0;
            }

            .scrollable-carousel-outer {
                padding: 0 var(--layout-spacing-48);
            }
        }

        .model-overview-other-stock-comparison-button {
            margin-top: calc(var(--layout-spacing-24) * -1);
        }

        .model-overview-other-stock-product {
            width: 14rem;
        }

        .slide-container {
            border-radius: var(--effects-radius-lg);
        }

        ${largeTablet(css`
            margin-bottom: 1.8rem; // Half button height

            .model-overview-wrapper {
                padding-top: var(--layout-spacing-24);
                gap: 0;
                grid-template-columns: ${getModelOverviewGridTemplateColumns({
                    hasImages,
                    isLoadingImages,
                    isSmallTablet: true,
                })};
            }

            .model-overview-heading {
                text-align: center;
            }

            .model-overview-carousel,
            .carousel-skeleton {
                width: 100%;
                margin: 0 0 var(--layout-spacing-24);
            }

            .model-overview-left {
                order: 2;
                max-width: 100%;
                padding: 0 var(--layout-spacing-8);
            }

            .model-overview-specs {
                padding-top: ${
                    hasSpecs
                        ? 'var(--layout-spacing-48)'
                        : 'var(--layout-spacing-24)'
                };
            }

            .model-overview-other-stock {
                margin-top: var(--layout-spacing-24);
                margin-bottom: calc(var(--layout-spacing-32) * -1);
                align-items: center;
                text-align: center;
            }

            .model-overview-other-stock-product-list {
                max-width: 100%;
            }

            .model-overview-other-stock-comparison-button {
                padding-bottom: var(--layout-spacing-24);
            }
        `)}

        ${smallMobile(css`
            .model-overview-other-stock-product-list ul li {
                padding: 0 calc(var(--layout-spacing-12) / 2);
            }
        `)}
    `;
    /* eslint-enable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
};

/**
 * The model overview component styles
 *
 * @param {StyledModelOverviewProps} props The model overview properties object
 * */
export const styles = (props: StyledModelOverviewProps) => css`
    ${baseStyles(props)}
`;
